import { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Smartphone, ArrowLeft, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Seo } from "@/components/Seo";
import { useI18n, LangToggle } from "@/context/I18nContext";

export default function ApplicatorLogin() {
  const { t } = useI18n();
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const [screen, setScreen] = useState("mobile"); // mobile | otp
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const timer = useRef(null);

  useEffect(() => () => clearInterval(timer.current), []);
  const startCooldown = (s) => {
    setResendIn(s);
    clearInterval(timer.current);
    timer.current = setInterval(() => setResendIn((v) => { if (v <= 1) { clearInterval(timer.current); return 0; } return v - 1; }), 1000);
  };

  const sendOtp = async () => {
    if (mobile.replace(/\D/g, "").length < 10) return toast.error(t("enter_valid_mobile") || "Enter a valid mobile number");
    setBusy(true);
    try {
      const { data } = await api.post("/applicator/auth/send-otp", { mobile });
      setScreen("otp"); startCooldown(data.resend_in || 30);
      toast.success(t("otp_sent") || "OTP sent");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send OTP"); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    if (otp.length < 4) return;
    setBusy(true);
    try {
      const { data } = await api.post("/applicator/auth/verify-otp", { mobile, code: otp });
      if (data.is_new) {
        toast.success(t("verified") || "Verified");
        navigate("/applicator/apply", { state: { mobile: data.mobile, verified_token: data.verified_token } });
      } else {
        await setSession(data.token);
        toast.success(t("welcome") || "Welcome");
        navigate("/applicator/dashboard");
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Verification failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex flex-col" data-testid="applicator-login-page">
      <Seo title="Applicator Login — ERRĀH" description="ERRĀH Certified Applicator mobile login." />
      <div className="flex justify-between items-center p-5">
        <Link to="/applicator-network"><img src="/errah-logo.png" alt="ERRĀH" className="h-12" /></Link>
        <LangToggle />
      </div>

      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <p className="font-display text-3xl tracking-[0.2em]">ERRĀH</p>
            <p className="errah-label gold-text mt-2">{t("certified_applicator_network") || "Certified Applicator Network"}</p>
          </div>

          {screen === "mobile" ? (
            <div data-testid="otp-mobile-screen">
              <div className="flex items-center gap-2 justify-center mb-5 text-[#C9A45C]"><Smartphone size={22} /></div>
              <label className="block text-center text-lg mb-4">{t("enter_mobile") || "अपना मोबाइल नंबर डालें / Enter your mobile number"}</label>
              <div className="flex items-center border-2 border-white/20 focus-within:border-[#C9A45C] bg-white/[0.03]">
                <span className="px-4 py-4 text-lg text-gray-400 border-r border-white/15">+91</span>
                <input autoFocus inputMode="numeric" value={mobile} onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                  data-testid="otp-mobile-input" placeholder="__________"
                  className="flex-1 bg-transparent px-4 py-4 text-xl tracking-widest outline-none" />
              </div>
              <button onClick={sendOtp} disabled={busy} data-testid="otp-send-btn"
                className="w-full mt-6 bg-[#C9A45C] text-black py-5 text-lg font-semibold hover:bg-[#E4C987] transition-colors disabled:opacity-60">
                {busy ? "…" : (t("send_otp") || "OTP भेजें / Send OTP")}
              </button>
            </div>
          ) : (
            <div data-testid="otp-verify-screen">
              <button onClick={() => setScreen("mobile")} data-testid="otp-change-number" className="text-sm text-gray-400 inline-flex items-center gap-1.5 mb-5"><ArrowLeft size={15} /> {t("change_number") || "Change Number"}</button>
              <label className="block text-center text-lg mb-1">{t("enter_otp") || "OTP डालें / Enter OTP"}</label>
              <p className="text-center text-xs text-gray-500 mb-4">+91 {mobile}</p>
              <input autoFocus inputMode="numeric" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                data-testid="otp-code-input" placeholder="______"
                className="w-full text-center bg-white/[0.03] border-2 border-white/20 focus:border-[#C9A45C] py-4 text-3xl tracking-[0.5em] outline-none" />
              <button onClick={verify} disabled={busy || otp.length < 4} data-testid="otp-verify-btn"
                className="w-full mt-6 bg-[#C9A45C] text-black py-5 text-lg font-semibold hover:bg-[#E4C987] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2">
                <ShieldCheck size={20} /> {busy ? "…" : (t("verify") || "सत्यापित करें / Verify")}
              </button>
              <button onClick={sendOtp} disabled={resendIn > 0} data-testid="otp-resend-btn"
                className="w-full mt-4 text-sm text-gray-400 disabled:opacity-50">
                {resendIn > 0 ? `${t("resend_otp") || "Resend OTP"} (${resendIn}s)` : (t("resend_otp") || "Resend OTP")}
              </button>
            </div>
          )}
          <p className="mt-8 text-center text-xs text-gray-600">{t("new_to_errah") || "New to ERRĀH? Enter your mobile to join."}</p>
        </div>
      </div>
    </div>
  );
}
